// Shared silence form for Threat Insights — scope, duration, reason.
// Used inline in ThreatInsightDrawer footer (LE-290).

import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Typography,
  Chip,
} from "@mui/material";

import colors from "../../../../utils/colors";
import { SafeTextField } from "../../../common/Greens";
import { CancelButton, SaveButton } from "../../../common/Button";
import { TEXT_PRIMARY, TEXT_SECONDARY } from "../detection/wafEventDrawerShared";

function getSubjectValue(finding) {
  if (!finding) return null;
  const evidence = finding.evidence || {};
  return (
    (evidence.subject_value && String(evidence.subject_value).trim()) ||
    (evidence.ips && evidence.ips[0] && String(evidence.ips[0]).trim()) ||
    null
  );
}

export default function SilenceThreatForm({
  finding,
  saving,
  onCancel,
  onSubmit,
}) {
  const [durationHours, setDurationHours] = React.useState(168);
  const [wildcard, setWildcard] = React.useState(true);
  const [reason, setReason] = React.useState("Noisy on this site");

  const findingId = finding?.id;
  React.useEffect(() => {
    setDurationHours(168);
    setWildcard(true);
    setReason("Noisy on this site");
  }, [findingId]);

  if (!finding) return null;

  const ruleName = finding.rule?.name || finding.rule_id || "this rule";
  const subjectValue = getSubjectValue(finding);

  const handleSubmit = () => {
    const hours = Number(durationHours);
    if (!Number.isFinite(hours) || hours < 1 || hours > 24 * 30) return;
    onSubmit({
      subject_value: wildcard ? null : subjectValue,
      duration_hours: hours,
      reason: reason.trim() || "Noisy on this site",
    });
  };

  const durationInvalid =
    !Number.isFinite(Number(durationHours)) ||
    Number(durationHours) < 1 ||
    Number(durationHours) > 24 * 30;

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.45 }}>
        The scanner will skip <b>{ruleName}</b> for this site until the suppression expires.
        The scanner honours this on its next tick (≤ 5 min).
      </Typography>

      <Box>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY, mb: 0.5 }}>
          Scope
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label="This site — every subject"
            onClick={() => setWildcard(true)}
            sx={{
              fontSize: 12,
              backgroundColor: wildcard ? colors.primary[100] : "#fff",
              color: wildcard ? colors.primary[300] : colors.neutral[800],
              border: `1px solid ${wildcard ? colors.primary[300] : colors.neutral[200]}`,
            }}
          />
          <Chip
            label={subjectValue ? `Only ${subjectValue}` : "Only this subject"}
            onClick={() => subjectValue && setWildcard(false)}
            disabled={!subjectValue}
            sx={{
              fontSize: 12,
              backgroundColor: !wildcard ? colors.primary[100] : "#fff",
              color: !wildcard ? colors.primary[300] : colors.neutral[800],
              border: `1px solid ${!wildcard ? colors.primary[300] : colors.neutral[200]}`,
            }}
          />
        </Stack>
      </Box>

      <SafeTextField
        label="Duration (hours)"
        type="number"
        size="small"
        value={durationHours}
        onChange={(e) => setDurationHours(e.target.value)}
        inputProps={{ min: 1, max: 24 * 30 }}
        helperText={durationInvalid ? "Enter 1–720 hours." : "Max 720 h (30 days). Auto-expires."}
        error={durationInvalid}
        sx={{
          backgroundColor: "#fff",
          "& .MuiOutlinedInput-root": { fontSize: 13 },
        }}
      />

      <SafeTextField
        label="Reason"
        size="small"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        multiline
        maxRows={3}
        inputProps={{ maxLength: 500 }}
        helperText="Stored on the suppression row for audit."
        sx={{
          backgroundColor: "#fff",
          "& .MuiOutlinedInput-root": { fontSize: 13 },
        }}
      />

      <Stack direction="row" spacing={1.5} justifyContent="flex-end" alignItems="center">
        <CancelButton
          onClick={onCancel}
          disabled={saving}
          sx={{ width: "auto", minWidth: 104, height: 36 }}
        />
        <SaveButton
          onClick={handleSubmit}
          disabled={durationInvalid}
          loading={saving}
          label="Silence"
          sx={{ width: "auto", minWidth: 104, height: 36 }}
        />
      </Stack>
    </Stack>
  );
}

SilenceThreatForm.propTypes = {
  finding: PropTypes.object.isRequired,
  saving: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

SilenceThreatForm.defaultProps = {
  saving: false,
};
